// Firestore service for Excise Tax Rules management (per PRD structure)
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'TaxRules';

// Validation constants
const VALID_CALCULATION_METHODS = ['specific', 'ad_valorem'];
const VALID_APPLICATION_POINTS = ['manufacturer', 'wholesale', 'retail'];
const VALID_DISPLAY_METHODS = ['included', 'separate', 'both'];
const VALID_OPERATORS = ['>', '<', '>=', '<=', '==', '!='];
const VALID_SHIPPING_TAX_METHODS = ['destination_based', 'origin_based'];
const VALID_CALCULATION_ORDERS = ['excise_then_sales', 'excise_separate', 'excise_only'];

export const taxRulesService = {
  // Get all excise tax rules with real-time updates
  subscribeToRules: (callback, options = {}) => {
    let q = query(
      collection(db, COLLECTION_NAME),
      orderBy('priority', 'desc'),
      orderBy('createdAt', 'desc')
    );
    
    // Add filters if specified
    if (options.activeOnly) {
      q = query(q, where('isActive', '==', true));
    }
    
    if (options.categoryId) {
      q = query(q, where('taxCategoryId', '==', options.categoryId));
    }
    
    return onSnapshot(q, (snapshot) => {
      const rules = [];
      snapshot.forEach((doc) => {
        rules.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(rules);
    });
  },

  // Get all rules as array
  getAll: async (options = {}) => {
    let q = query(
      collection(db, COLLECTION_NAME),
      orderBy('priority', 'desc'),
      orderBy('createdAt', 'desc')
    );
    
    // Add filters if specified
    if (options.activeOnly) {
      q = query(q, where('isActive', '==', true));
    }
    
    if (options.categoryId) {
      q = query(q, where('taxCategoryId', '==', options.categoryId));
    }
    
    const snapshot = await getDocs(q);
    
    const rules = [];
    snapshot.forEach((doc) => {
      rules.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return rules;
  },

  // Get single rule
  getRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    return null;
  },

  // Create new excise tax rule (enhanced structure per PRD)
  createRule: async (ruleData) => {
    const ruleId = ruleData.id || `excise_${Date.now()}`;
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    const newRule = {
      // Rule identification
      name: ruleData.name || '',
      description: ruleData.description || '',
      
      // Category linkage to pure taxonomy
      taxCategoryId: ruleData.taxCategoryId,
      taxCategoryPath: ruleData.taxCategoryPath || '',
      
      // Geographic targeting
      jurisdiction: {
        country: ruleData.jurisdiction?.country || 'US',
        state: ruleData.jurisdiction?.state || null,
        counties: ruleData.jurisdiction?.counties || [],
        cities: ruleData.jurisdiction?.cities || [],
        zipCodes: ruleData.jurisdiction?.zipCodes || [],
        excludeZipCodes: ruleData.jurisdiction?.excludeZipCodes || []
      },
      
      // Tax calculation
      calculationMethod: ruleData.calculationMethod || 'specific', // 'specific' | 'ad_valorem'
      rate: ruleData.rate || 0,
      unit: ruleData.unit || 'item',
      
      // Tiered rates (for complex rules)
      tiers: ruleData.tiers || [],
      
      // Product attribute conditions
      conditions: ruleData.conditions || [],
      
      // Ad valorem configuration
      adValoremRate: ruleData.adValoremRate || null,
      adValoremConditions: ruleData.adValoremConditions || [],
      
      // Application settings
      applicationPoint: ruleData.applicationPoint || 'retail', // 'manufacturer' | 'wholesale' | 'retail'
      applyBeforeDiscounts: ruleData.applyBeforeDiscounts !== undefined ? ruleData.applyBeforeDiscounts : true,
      includeInSalesTaxBase: ruleData.includeInSalesTaxBase !== undefined ? ruleData.includeInSalesTaxBase : true,
      calculationOrder: ruleData.calculationOrder || 'excise_then_sales', // 'excise_then_sales' | 'excise_separate' | 'excise_only'
      
      // Shipping treatment
      shippingTreatment: {
        applyToShipping: ruleData.shippingTreatment?.applyToShipping || false,
        mustPrepay: ruleData.shippingTreatment?.mustPrepay || false,
        requiresStamps: ruleData.shippingTreatment?.requiresStamps || false,
        shippingTaxMethod: ruleData.shippingTreatment?.shippingTaxMethod || 'destination_based'
      },
      
      // Display settings
      displayMethod: ruleData.displayMethod || 'included', // 'included' | 'separate' | 'both'
      
      // Metadata
      effectiveDate: ruleData.effectiveDate || null,
      expirationDate: ruleData.expirationDate || null,
      isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
      priority: ruleData.priority || 50,
      dataSource: ruleData.dataSource || 'manual',
      lastVerified: ruleData.lastVerified || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: ruleData.createdBy || 'admin@lithlan.com'
    };
    
    await setDoc(docRef, newRule);
    return { id: ruleId, ...newRule };
  },

  // Update existing rule
  updateRule: async (ruleId, updateData) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    const updates = {
      ...updateData,
      updatedAt: serverTimestamp(),
      lastVerified: new Date().toISOString()
    };
    
    await updateDoc(docRef, updates);
    return updates;
  },

  // Archive rule (soft delete)
  archiveRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    await updateDoc(docRef, {
      isActive: false,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  },

  // Restore archived rule
  restoreRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    await updateDoc(docRef, {
      isActive: true,
      archivedAt: null,
      updatedAt: serverTimestamp()
    });
  },

  // Delete rule permanently
  deleteRule: async (ruleId) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    await deleteDoc(docRef);
  },

  // Get rules by category
  getRulesByCategory: async (categoryId) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('taxCategoryId', '==', categoryId),
      where('isActive', '==', true),
      orderBy('priority', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const rules = [];
    snapshot.forEach((doc) => {
      rules.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return rules;
  },

  // Get rules by jurisdiction
  getRulesByJurisdiction: async (jurisdiction) => {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('jurisdiction.country', '==', jurisdiction.country)
    );
    
    if (jurisdiction.state) {
      q = query(q, where('jurisdiction.state', '==', jurisdiction.state));
    }
    
    const snapshot = await getDocs(q);
    const rules = [];
    snapshot.forEach((doc) => {
      const ruleData = doc.data();
      
      // Additional filtering for county/city if needed
      if (jurisdiction.county && ruleData.jurisdiction.county && ruleData.jurisdiction.county !== jurisdiction.county) {
        return;
      }
      if (jurisdiction.city && ruleData.jurisdiction.city && ruleData.jurisdiction.city !== jurisdiction.city) {
        return;
      }
      
      rules.push({
        id: doc.id,
        ...ruleData
      });
    });
    
    return rules.sort((a, b) => b.priority - a.priority);
  },

  // Update rule priority
  updatePriority: async (ruleId, priority) => {
    const docRef = doc(db, COLLECTION_NAME, ruleId);
    
    await updateDoc(docRef, {
      priority,
      updatedAt: serverTimestamp()
    });
  },

  // Bulk update priorities
  updatePriorities: async (updates) => {
    const batch = writeBatch(db);
    
    updates.forEach(({ ruleId, priority }) => {
      const docRef = doc(db, COLLECTION_NAME, ruleId);
      batch.update(docRef, { 
        priority, 
        updatedAt: serverTimestamp() 
      });
    });
    
    await batch.commit();
  },

  // Search rules
  searchRules: async (searchTerm) => {
    // Note: Firestore doesn't support full-text search natively
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const rules = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const searchString = `${data.name} ${data.description} ${data.taxCategoryPath}`.toLowerCase();
      
      if (searchString.includes(searchTerm.toLowerCase())) {
        rules.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return rules;
  },

  // Validate rule conflicts
  checkRuleConflicts: async (newRule) => {
    const conflicts = [];
    
    // Get all rules for the same category and jurisdiction
    const existingRules = await taxRulesService.getRulesByCategory(newRule.taxCategoryId);
    
    for (const rule of existingRules) {
      if (rule.id === newRule.id) continue; // Skip self when updating
      
      // Check jurisdiction overlap
      if (hasJurisdictionOverlap(rule.jurisdiction, newRule.jurisdiction)) {
        // Check date overlap
        if (hasDateOverlap(rule, newRule)) {
          conflicts.push({
            ruleId: rule.id,
            ruleName: rule.name,
            type: 'jurisdiction_date_overlap',
            message: `Overlaps with rule "${rule.name}" in jurisdiction and date range`
          });
        }
      }
    }
    
    return conflicts;
  },

  // Get applicable rules for a product/transaction
  getApplicableRules: async (categoryId, jurisdiction, transactionDate = new Date()) => {
    const categoryRules = await taxRulesService.getRulesByCategory(categoryId);
    const applicableRules = [];
    
    for (const rule of categoryRules) {
      // Check if rule is active
      if (!rule.isActive) continue;
      
      // Check effective date
      if (rule.effectiveDate && new Date(rule.effectiveDate) > transactionDate) continue;
      
      // Check expiration date
      if (rule.expirationDate && new Date(rule.expirationDate) < transactionDate) continue;
      
      // Check jurisdiction match
      if (matchesJurisdiction(rule.jurisdiction, jurisdiction)) {
        applicableRules.push(rule);
      }
    }
    
    // Sort by priority (highest first)
    return applicableRules.sort((a, b) => b.priority - a.priority);
  },

  // Validate tax rule data
  validateRule: (ruleData) => {
    const errors = [];
    
    // Required fields validation
    if (!ruleData.name || ruleData.name.trim() === '') {
      errors.push('Name is required');
    }
    
    if (!ruleData.taxCategoryId) {
      errors.push('Tax category is required');
    }
    
    if (!VALID_CALCULATION_METHODS.includes(ruleData.calculationMethod)) {
      errors.push('Invalid calculation method');
    }
    
    if (ruleData.applicationPoint && !VALID_APPLICATION_POINTS.includes(ruleData.applicationPoint)) {
      errors.push('Invalid application point');
    }
    
    if (ruleData.displayMethod && !VALID_DISPLAY_METHODS.includes(ruleData.displayMethod)) {
      errors.push('Invalid display method');
    }
    
    if (ruleData.calculationOrder && !VALID_CALCULATION_ORDERS.includes(ruleData.calculationOrder)) {
      errors.push('Invalid calculation order');
    }
    
    // Rate validation
    if (ruleData.calculationMethod === 'specific' && (!ruleData.rate || ruleData.rate <= 0)) {
      errors.push('Rate must be greater than 0 for specific calculations');
    }
    
    if (ruleData.calculationMethod === 'ad_valorem' && (!ruleData.rate || ruleData.rate <= 0 || ruleData.rate > 100)) {
      errors.push('Ad valorem rate must be between 0 and 100');
    }
    
    // Tiers validation
    if (ruleData.tiers && Array.isArray(ruleData.tiers)) {
      ruleData.tiers.forEach((tier, index) => {
        if (tier.maxQuantity !== null && tier.minQuantity >= tier.maxQuantity) {
          errors.push('Tier maximum quantity must be greater than minimum');
        }
        
        if (!tier.rate || tier.rate <= 0) {
          errors.push(`Tier ${index + 1}: rate must be greater than 0`);
        }
      });
    }
    
    // Conditions validation
    if (ruleData.conditions && Array.isArray(ruleData.conditions)) {
      ruleData.conditions.forEach((condition, index) => {
        if (!condition.field || condition.field.trim() === '') {
          errors.push('Condition field is required');
        }
        
        if (!VALID_OPERATORS.includes(condition.operator)) {
          errors.push('Invalid condition operator');
        }
        
        if (condition.value === null || condition.value === undefined) {
          errors.push(`Condition ${index + 1}: value is required`);
        }
      });
    }
    
    // Shipping treatment validation
    if (ruleData.shippingTreatment) {
      if (ruleData.shippingTreatment.shippingTaxMethod && 
          !VALID_SHIPPING_TAX_METHODS.includes(ruleData.shippingTreatment.shippingTaxMethod)) {
        errors.push('Invalid shipping tax method');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Calculate tax for an item using a rule
  calculateTax: (rule, item) => {
    let taxAmount = 0;
    let calculation = {
      method: rule.calculationMethod,
      appliedRate: rule.rate,
      appliedQuantity: item.quantity || 1,
      taxableAmount: item.price || 0,
      tierBreakdown: []
    };
    
    try {
      if (rule.calculationMethod === 'specific') {
        if (rule.tiers && rule.tiers.length > 0) {
          // Tiered calculation
          const totalQuantity = item.quantity || 1;
          
          // Simple approach: tiers represent ranges
          // Tier 1: 0-10 means items 1 through 10 get this rate
          // Tier 2: 11+ means items 11 and above get this rate
          
          rule.tiers.forEach(tier => {
            let tierQuantity = 0;
            
            if (tier.maxQuantity === null) {
              // This tier applies to quantities above minQuantity
              if (totalQuantity > tier.minQuantity) {
                tierQuantity = totalQuantity - (tier.minQuantity - 1);
              }
            } else {
              // This tier applies to quantities between minQuantity and maxQuantity (inclusive)
              if (totalQuantity > tier.minQuantity) {
                const effectiveMax = Math.min(tier.maxQuantity, totalQuantity);
                tierQuantity = effectiveMax - tier.minQuantity;
              }
            }
            
            if (tierQuantity > 0) {
              const tierTax = tierQuantity * tier.rate;
              taxAmount += tierTax;
              
              calculation.tierBreakdown.push({
                tier: tier,
                quantity: tierQuantity,
                rate: tier.rate,
                taxAmount: tierTax
              });
            }
          });
        } else {
          // Simple specific calculation
          taxAmount = (item.quantity || 1) * rule.rate;
        }
      } else if (rule.calculationMethod === 'ad_valorem') {
        // Percentage-based calculation
        const taxableAmount = item.price || 0;
        taxAmount = (taxableAmount * rule.rate) / 100;
        calculation.taxableAmount = taxableAmount;
      }
      
      // Apply ad valorem additional rate if conditions are met
      if (rule.adValoremRate && rule.adValoremConditions) {
        const conditionsMet = rule.adValoremConditions.every(condition => {
          return taxRulesService.evaluateCondition(condition, item);
        });
        
        if (conditionsMet) {
          const additionalTax = (item.price || 0) * (rule.adValoremRate / 100);
          taxAmount += additionalTax;
          calculation.adValoremTax = additionalTax;
        }
      }
      
    } catch (error) {
      console.error('Tax calculation error:', error);
      return {
        taxAmount: 0,
        calculation: { ...calculation, error: error.message },
        success: false
      };
    }
    
    return {
      taxAmount: Math.round(taxAmount * 100) / 100, // Round to 2 decimal places
      calculation,
      success: true
    };
  },

  // Evaluate a condition against an item
  evaluateCondition: (condition, item) => {
    const fieldValue = taxRulesService.getNestedProperty(item, condition.field);
    
    if (fieldValue === undefined || fieldValue === null) {
      return false;
    }
    
    switch (condition.operator) {
      case '>':
        return fieldValue > condition.value;
      case '<':
        return fieldValue < condition.value;
      case '>=':
        return fieldValue >= condition.value;
      case '<=':
        return fieldValue <= condition.value;
      case '==':
        return fieldValue === condition.value;
      case '!=':
        return fieldValue !== condition.value;
      default:
        return false;
    }
  },

  // Helper function to get nested property from object
  getNestedProperty: (obj, path) => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }
};

// Helper functions

function hasJurisdictionOverlap(j1, j2) {
  // Basic jurisdiction overlap check
  if (j1.country !== j2.country) return false;
  
  // If both have states specified, they must match
  if (j1.state && j2.state && j1.state !== j2.state) return false;
  
  // If one is broader (no state) and other is specific, they overlap
  return true;
}

function hasDateOverlap(rule1, rule2) {
  const r1Start = rule1.effectiveDate ? new Date(rule1.effectiveDate) : new Date(0);
  const r1End = rule1.expirationDate ? new Date(rule1.expirationDate) : new Date('2099-12-31');
  
  const r2Start = rule2.effectiveDate ? new Date(rule2.effectiveDate) : new Date(0);
  const r2End = rule2.expirationDate ? new Date(rule2.expirationDate) : new Date('2099-12-31');
  
  return r1Start <= r2End && r2Start <= r1End;
}

function matchesJurisdiction(ruleJurisdiction, targetJurisdiction) {
  // Country must match
  if (ruleJurisdiction.country !== targetJurisdiction.country) return false;
  
  // If rule has no state specified, it applies to all states
  if (!ruleJurisdiction.state) return true;
  
  // State must match
  if (ruleJurisdiction.state !== targetJurisdiction.state) return false;
  
  // If rule has no county specified, it applies to all counties in the state
  if (!ruleJurisdiction.county || ruleJurisdiction.counties?.includes('*')) return true;
  
  // County must match (if specified)
  if (ruleJurisdiction.county && ruleJurisdiction.county !== targetJurisdiction.county) return false;
  
  // Similar logic for city and zip code...
  return true;
}

export default taxRulesService;